import { NotifierTypes } from 'constants/notifierConstants';
import { selectCurrentTeam, selectInvoiceLimit, selectIsActionsBlocked, showNotifier } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import React, { FC, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Lottie from 'lottie-react';
import routesPath from 'routes/RoutesPath';
import { InvoicesRouteState } from 'routes/types';
import { LoadingSpinnerAnimation } from 'assets/animations';
import GenericError from 'components/genric-error/GenericError';
import { useEditInvoiceV2Mutation, useLazyGetInvoiceByIdV2Query } from '../api';
import { PreviewBar } from '../components';
import { InvoiceTabEnum, minimumLineItemsError } from '../create-invoice/constants';
import { EditInvoicePayloadV2, TaxesAndFeesResponse } from '../invoices.model';
import InvoiceOptionsSectionEdit from './components/InvoiceOptionsSectionEdit';
import InvoicesTabSideNav from '../create-invoice/components/InvoicesTabSideNav';
import LineItemsSectionEdit from './components/LineItemsSectionEdit';
import NotesAttachmentsSectionEdit from './components/NotesAttachmentsSectionEdit';
import PaymentOptionsSectionEdit from './components/PaymentOptionsSectionEdit';
import InvoiceFormRemindersSection from '../create-invoice/components/InvoiceFormRemindersSection';
import { throttle } from 'utils/commonUtils';
import {
  addInvoiceValidationErrorEditPage,
  enableCustomerNotSelectedErrorEditPage,
  initialiseInvoicePaymentModesEdit,
  resetInvoiceEditSlice,
  selectEstimateNumberForInvoiceEditPage,
  selectEstimateStatusForInvoiceEditPage,
  selectInvoiceEditPage,
  selectInvoiceFinalAmountEditPage,
  selectInvoicePaymentModesEdit,
  selectInvoiceReferenceNumberEditPage,
  selectInvoiceSliceInitializationInProgress,
  selectIsBackFromCreateTaxInEditSlice,
  selectIsFromPreview,
  selectIsInvoiceFromJobEditPage,
  selectIsSignatureEnabledEditPage,
  selectJobDataForInvoiceEditPage,
  selectJobNumberForInvoiceEditPage,
  selectPartialPaymentEnabledEditPage,
  toggleIsFromPreview,
  updateInvoiceEditedResponse,
  updateInvoiceInitializationInProgress,
  updateInvoiceReferenceNumberEditPage,
  updateIsSignatureEnabledEditPage,
  updatePartialPaymentEnabledEditPage,
  updateSelectedTNC
} from './invoicesEditSlice';
import { updateInvoiceDefaultReminderFromSetting } from '../InvoicesSlice';
import { ErrorResponse } from 'containers/app/app.model';
import {
  checkIfAnyPaymentModeIsEligible,
  checkIfInvoiceTotalExceedsPaymentModeLimit,
  hasChangesToSync
} from '../utils';
import { DispatchDestination, invoiceMinimumAmount } from 'constants/common';
import { useGetItemSuggestionsAndGlobalTaxesAndFeesQuery } from 'containers/product-catalog/api';
import MoreOptionsSection from '../create-invoice/components/MoreOptionsSection';
import { useGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { FeatureToggleState, PartialPaymentOption } from 'containers/settings/constants';
import { useLazyGetInvoiceSettingsQuery } from 'containers/settings/invoice-settings/api';
import { transformSettingApiToComponentReminders } from 'containers/settings/utils';
import { getEditInvoiceV2Payload } from './util';
import EditInvoiceCustomer from './components/EditInvoiceCustomer';
import { CrossIconvector } from 'assets/icons';
import { GenericModal } from 'components';
import NoSelfCheckoutPopup from '../components/NoSelfCheckoutPopup';
import SendUpdateModal from '../components/SendUpdateModal';
import { InvoiceErrorFieldsDefault } from '../create-invoice/types';
import InvoiceErrorsPopup from '../components/InvoiceErrorsPopup';
import InvoiceLimitExceededPopup from '../components/InvoiceLimitExceededPopup';
import PartialPaymentAvailablePopup from '../components/PartialPaymentAvailablePopup';
import { updateCustomizations } from 'containers/settings/invoiceSettingsSlice';
import { EstimateStatus } from 'containers/estimates/constants';
import BlockedInfoPopup from 'containers/home/components/BlockedInfoPopup';
import InvalidInvoiceTotalModal from '../components/InvalidInvoiceTotalModal';
import { selectInvoiceByIdRawDataV2 } from 'containers/payments-side-panel/sidePanelSlice';

interface IEditInvoiceContext {
  selectedInvoiceTab: InvoiceTabEnum;
  setSelectedInvoiceTab: React.Dispatch<React.SetStateAction<InvoiceTabEnum>>;
}

export const EditInvoiceContext = createContext<IEditInvoiceContext>({
  selectedInvoiceTab: InvoiceTabEnum.BILL_TO,
  setSelectedInvoiceTab: null
});

interface EditInvoiceProps {
  goToPreviewPage: () => void;
  taxesAndFeesData: TaxesAndFeesResponse;
  isLoadingTaxesAndFees: boolean;
}

const EditInvoice: FC<EditInvoiceProps> = ({ goToPreviewPage, taxesAndFeesData, isLoadingTaxesAndFees }) => {
  const invoiceInitInProgress = useAppSelector(selectInvoiceSliceInitializationInProgress);
  const isBackFromCreateTaxRoute = useAppSelector(selectIsBackFromCreateTaxInEditSlice);
  const selectedIsSignatureEnabled = useAppSelector(selectIsSignatureEnabledEditPage);
  const invoiceReferenceNumberEditPage = useAppSelector(selectInvoiceReferenceNumberEditPage);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);
  const selectedInvoice = useAppSelector(selectInvoiceEditPage);
  const isFromPreview = useAppSelector(selectIsFromPreview);
  const isInvoiceFromJob = useAppSelector(selectIsInvoiceFromJobEditPage);
  const jobData = useAppSelector(selectJobDataForInvoiceEditPage);
  const invoiceFromJobNumber = useAppSelector(selectJobNumberForInvoiceEditPage);
  const estimateNumber = useAppSelector(selectEstimateNumberForInvoiceEditPage);
  const estimateStatus = useAppSelector(selectEstimateStatusForInvoiceEditPage);
  const isEstimatePending = useMemo(() => estimateStatus?.toUpperCase() === EstimateStatus.PENDING, [estimateStatus]);
  const partialPaymentEnabled = useAppSelector(selectPartialPaymentEnabledEditPage);
  const invoicePaymentModes = useAppSelector(selectInvoicePaymentModesEdit);
  const invoiceTotal = useAppSelector(selectInvoiceFinalAmountEditPage);
  const invoiceLimits = useAppSelector(selectInvoiceLimit);
  const invoiceRawDataV2 = useAppSelector(selectInvoiceByIdRawDataV2);

  const isInitialisationDone = useRef(false);

  const hasCustomerEmail = useMemo(
    () => selectedInvoice.invoiceCustomerEditPage?.email?.trim() !== '',
    [selectedInvoice.invoiceCustomerEditPage?.email]
  );

  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [showSyncConfirmationPopup, setShowSyncConfirmationPopup] = useState(false);
  const [showNoSelfCheckoutPopup, setShowNoSelfCheckoutPopup] = useState(false);
  const [showInvoiceErrorsPopup, setShowInvoiceErrorsPopup] = useState({
    open: false,
    errors: InvoiceErrorFieldsDefault
  });
  const [showMerchantBlockedPopup, setShowMerchantBlockedPopup] = useState(false);
  const [showPartialPaymentAvailablePopup, setShowPartialPaymentAvailablePopup] = useState(false);
  const [showInvoiceLimitExceededPopup, setShowInvoiceLimitExceededPopup] = useState(false);
  const [showInvalidTotalModal, setShowInvalidTotalModal] = useState(false);
  const [showSendUpdateToCustomerModal, setShowSendUpdateToCustomerModal] = useState(false);
  const [getInvoiceQuery, getInvoiceQueryState] = useLazyGetInvoiceByIdV2Query();
  const [getInvoiceSettings] = useLazyGetInvoiceSettingsQuery();

  useGetItemSuggestionsAndGlobalTaxesAndFeesQuery({
    teamId: currentTeam.id,
    profession: currentTeam.profession,
    dispatchDestination: DispatchDestination.EDIT_INVOICE_SLICE
  });

  const { data: merchantCustomisations, isSuccess: merchantCustomisationsSuccess } = useGetMerchantCustomizationsQuery({
    teamId: currentTeam.id.toString()
  });

  useEffect(() => {
    if (
      merchantCustomisationsSuccess &&
      selectedInvoice?.finalAmountEditPage &&
      !isFromPreview &&
      !isInitialisationDone.current
    ) {
      dispatch(updateCustomizations(merchantCustomisations));
      if (!invoiceInitInProgress) {
        if (merchantCustomisations.customerSignatureConfig === FeatureToggleState.ALWAYS_ON) {
          dispatch(updateIsSignatureEnabledEditPage(true));
        }
        dispatch(
          initialiseInvoicePaymentModesEdit({
            paymentModes: merchantCustomisations.paymentModes,
            finalAmount: selectedInvoice.finalAmountEditPage
          })
        );
      }
      isInitialisationDone.current = true;
    }
  }, [merchantCustomisationsSuccess, invoiceInitInProgress, dispatch, selectedInvoice?.finalAmountEditPage]);

  // Fetch invoice settings on mount
  useEffect(() => {
    if (currentTeam.id) {
      getInvoiceSettings({ teamId: currentTeam.id }).then(response => {
        if (response.data) {
          // Transform invoice reminders to component format using existing utility function
          const defaultReminders = transformSettingApiToComponentReminders(response.data.reminders || [], false);
          const settingsRemindersEnabled = response.data.remindersEnabled || false;
          const forcePartialPaymentSettings = response.data.partialPaymentStatus === PartialPaymentOption.ALWAYS_ON;
          // Store default reminders and settings config in Redux
          dispatch(
            updateInvoiceDefaultReminderFromSetting({
              reminders: defaultReminders,
              enabled: settingsRemindersEnabled,
              partialPayment: response.data.partialPaymentStatus
            })
          );
          if (forcePartialPaymentSettings) {
            dispatch(updatePartialPaymentEnabledEditPage(true));
          }
        }
      });
    }
  }, [currentTeam.id, dispatch, getInvoiceSettings]);

  const [
    editInvoiceV2Mutation,
    {
      isSuccess: isEditInvoiceSuccess,
      data: invoiceEditedResponse,
      isError: isInvoiceEditError,
      error: invoiceEditError,
      isLoading: isEditInvoiceLoading
    }
  ] = useEditInvoiceV2Mutation();
  const navigate = useNavigate();
  const { invoiceId } = useParams();

  const [selectedInvoiceTab, setSelectedInvoiceTab] = useState(InvoiceTabEnum.BILL_TO);

  const { data: getInvoiceResponse, isSuccess: getInvoiceSuccess, isError: isGetInvoiceErr } = getInvoiceQueryState;

  // Calculate net paid amount (total paid minus tip) for validation
  // Use invoiceRawDataV2 from Redux (persists across navigation) with fallback to getInvoiceResponse
  const netPaidAmount = useMemo(() => {
    const invoiceData = invoiceRawDataV2?.invoice || getInvoiceResponse?.invoice;
    const totalPaidAmount = invoiceData?.totalPaidAmount || 0;
    const tipAmount = invoiceData?.tipAmount || 0;
    return totalPaidAmount - tipAmount;
  }, [
    invoiceRawDataV2?.invoice?.totalPaidAmount,
    invoiceRawDataV2?.invoice?.tipAmount,
    getInvoiceResponse?.invoice?.totalPaidAmount,
    getInvoiceResponse?.invoice?.tipAmount
  ]);

  useEffect(() => {
    if (!isFromPreview && !isBackFromCreateTaxRoute && !isLoadingTaxesAndFees) {
      dispatch(updateInvoiceInitializationInProgress(true));
      getInvoiceQuery({ invoiceId: invoiceId as string, isFromEdit: true, teamId: currentTeam.id });
    } else {
      setIsLoading(false);
      dispatch(toggleIsFromPreview(false));
    }
  }, [invoiceId, isLoadingTaxesAndFees]);

  useEffect(() => {
    if (getInvoiceResponse) {
      setIsLoading(false);
    }
  }, [getInvoiceResponse]);

  useEffect(() => {
    if (isGetInvoiceErr) {
      dispatch(updateInvoiceInitializationInProgress(false));
      setIsLoading(false);
    }
  }, [isGetInvoiceErr]);

  useEffect(() => {
    if (getInvoiceSuccess && getInvoiceResponse && !getInvoiceResponse?.invoice?.isModifiable) {
      navigate(-1);
    }
    if (getInvoiceSuccess && getInvoiceResponse) {
      dispatch(updateSelectedTNC(getInvoiceResponse.invoice.tnc));
    }
  }, [getInvoiceResponse, getInvoiceSuccess]);

  useEffect(() => {
    if (isEditInvoiceSuccess) {
      dispatch(resetInvoiceEditSlice());

      dispatch(updateInvoiceEditedResponse(invoiceEditedResponse));
      const invoiceRouteState: InvoicesRouteState = {
        showShareModal: true,
        isEditInvoiceShare: true,
        showSidePanel: false
      };
      navigate(routesPath.INVOICES, { replace: true, state: invoiceRouteState });
    }
  }, [isEditInvoiceSuccess]);

  const handleScrollToTab = (invoiceTab: InvoiceTabEnum) => {
    const tabElem = document.getElementById(invoiceTab);
    if (tabElem) {
      tabElem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
  };

  useEffect(() => {
    handleScrollToTab(selectedInvoiceTab);
  }, [selectedInvoiceTab]);

  useEffect(() => {
    if (isInvoiceEditError) {
      const error = invoiceEditError as ErrorResponse;

      if (error?.data?.details) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: error.data.details,
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      } else {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Something went wrong',
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      }
    }
  }, [isInvoiceEditError]);

  const hasDataChangesInInvoice = useMemo(
    () =>
      hasChangesToSync(
        selectedInvoice.invoiceCustomerEditPage,
        selectedInvoice.initialInvoiceCustomer,
        selectedInvoice.invoiceLineItemsEditPage,
        selectedInvoice.initialInvoiceLineItemsEditPage,
        selectedInvoice.selectedServiceCharge,
        selectedInvoice.initialServiceCharge,
        selectedInvoice.invoiceSubTotalEditPage,
        selectedInvoice.initialSubTotalEditPage,
        selectedInvoice.isServiceFeeEnabledEditPage,
        selectedInvoice.isServiceFeeEnabledEditPageInitial,
        selectedInvoice.currentSurchargeEditPage,
        selectedInvoice.initialSurchargeStateEditPage
      ),
    [
      selectedInvoice.invoiceCustomerEditPage,
      selectedInvoice.initialInvoiceCustomer,
      selectedInvoice.invoiceLineItemsEditPage,
      selectedInvoice.initialInvoiceLineItemsEditPage,
      selectedInvoice.selectedServiceCharge,
      selectedInvoice.initialServiceCharge,
      selectedInvoice.invoiceSubTotalEditPage,
      selectedInvoice.initialSubTotalEditPage,
      selectedInvoice.isServiceFeeEnabledEditPage,
      selectedInvoice.isServiceFeeEnabledEditPageInitial,
      selectedInvoice.currentSurchargeEditPage,
      selectedInvoice.initialSurchargeStateEditPage
    ]
  );

  const onEditAndShare = useCallback(() => {
    if (isActionsBlocked) {
      setShowMerchantBlockedPopup(true);
      return;
    }
    let isPageIncomplete = false;
    let errors = InvoiceErrorFieldsDefault;
    if (selectedInvoice.invoiceLineItemsEditPage.length === 0) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }

    if (selectedInvoice.invoiceCustomerEditPage === null) {
      isPageIncomplete = true;
      errors = { ...errors, customer: true };
    }
    const invoiceMinAmount = +merchantCustomisations?.minimumAmount || invoiceMinimumAmount;
    const invoiceMaxAmount = +merchantCustomisations?.maximumAmount;
    if (selectedInvoice.finalAmountEditPage < invoiceMinAmount) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    if (selectedInvoice.finalAmountEditPage > invoiceMaxAmount && !partialPaymentEnabled) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    // Validate if invoice total is less than amount already paid
    if (netPaidAmount > 0 && selectedInvoice.finalAmountEditPage < netPaidAmount) {
      setShowInvalidTotalModal(true);
      return;
    }

    const isSelfCheckoutEmpty =
      selectedInvoice.invoicePaymentModesEdit.filter(item => item.isSelected && item.teamLevelVisiblity).length === 0;
    if (isSelfCheckoutEmpty) {
      const isAnyPaymentModeEligible = checkIfAnyPaymentModeIsEligible(
        merchantCustomisations.paymentModes,
        selectedInvoice.finalAmountEditPage,
        partialPaymentEnabled
      );
      if (!isAnyPaymentModeEligible) {
        isPageIncomplete = true;
        errors = { ...errors, paymentMethods: true };
      }
    }

    if (isPageIncomplete) {
      setShowInvoiceErrorsPopup({ open: true, errors });
      return;
    }

    if (isSelfCheckoutEmpty) {
      setShowNoSelfCheckoutPopup(true);
      return;
    }

    // Check if partial payment is enabled but invoice total exceeds invoice limit
    if (partialPaymentEnabled && invoiceTotal > 0 && invoiceTotal > +invoiceLimits.invoiceMaxLimit) {
      setShowInvoiceLimitExceededPopup(true);
      return;
    }

    if (partialPaymentEnabled && invoiceTotal > 0) {
      const exceedsLimit = checkIfInvoiceTotalExceedsPaymentModeLimit(invoiceTotal, invoicePaymentModes);
      if (exceedsLimit) {
        setShowPartialPaymentAvailablePopup(true);
        return;
      }
    }

    if (
      ((isInvoiceFromJob && jobData[0]?.isEditable) || (estimateNumber && isEstimatePending)) &&
      hasDataChangesInInvoice
    ) {
      setShowSyncConfirmationPopup(true);
    } else if (hasCustomerEmail) {
      setShowSendUpdateToCustomerModal(true);
    } else {
      handleEditInvoice();
    }
  }, [
    isActionsBlocked,
    selectedInvoice.invoiceLineItemsEditPage.length,
    selectedInvoice.invoiceCustomerEditPage,
    selectedInvoice.finalAmountEditPage,
    merchantCustomisations?.minimumAmount,
    merchantCustomisations?.maximumAmount,
    partialPaymentEnabled,
    selectedInvoice.invoicePaymentModesEdit,
    merchantCustomisations?.paymentModes,
    invoiceTotal,
    invoiceLimits.invoiceMaxLimit,
    invoicePaymentModes,
    isInvoiceFromJob,
    jobData,
    estimateNumber,
    isEstimatePending,
    hasDataChangesInInvoice,
    netPaidAmount
  ]);

  const handleEditInvoice = (sendEmailToCustomer = false) => {
    const v2Payload = getEditInvoiceV2Payload(
      selectedInvoice,
      currentTeam,
      {
        enabled: getInvoiceResponse?.invoice?.tnc?.enabled || false,
        tncId: getInvoiceResponse?.invoice?.tnc?.tncId || 0
      },
      merchantCustomisations,
      sendEmailToCustomer
    );

    const editInvoicePayloadV2: EditInvoicePayloadV2 = {
      invoiceId: invoiceId as string,
      invoicePayload: v2Payload
    };

    editInvoiceV2Mutation({ teamId: currentTeam.id.toString(), payload: editInvoicePayloadV2 });
  };

  const handleUpdateToCustomerAndSave = (sendUpdateToCustomer = false) => {
    setShowSendUpdateToCustomerModal(false);
    handleEditInvoice(sendUpdateToCustomer);
  };

  const handleContinueFromSyncConfirmation = () => {
    setShowSyncConfirmationPopup(false);
    if (hasCustomerEmail) {
      setShowSendUpdateToCustomerModal(true);
    } else {
      handleEditInvoice();
    }
  };

  const handleOnCreateInvoiceWithoutSelfCheckout = useCallback(() => {
    if ((isInvoiceFromJob || (estimateNumber && isEstimatePending)) && hasDataChangesInInvoice) {
      setShowSyncConfirmationPopup(true);
    } else if (hasCustomerEmail) {
      setShowSendUpdateToCustomerModal(true);
    } else {
      handleEditInvoice();
    }
  }, [hasDataChangesInInvoice, isEstimatePending, estimateNumber, isInvoiceFromJob]);

  const handleOnEditPaymentMethods = useCallback(() => {
    handleCloseNoSelfCheckoutPopup();
    handleScrollToTab(InvoiceTabEnum.PAYMENT_OPTIONS);
  }, []);

  const handleCloseNoSelfCheckoutPopup = useCallback(() => {
    setShowNoSelfCheckoutPopup(false);
  }, []);

  const handleCloseInvoiceErrorsPopup = useCallback(() => {
    setShowInvoiceErrorsPopup({ open: false, errors: InvoiceErrorFieldsDefault });
  }, []);

  const handleClosePartialPaymentAvailablePopup = useCallback(() => {
    setShowPartialPaymentAvailablePopup(false);
  }, []);

  const handleSaveAnyway = useCallback(() => {
    handleClosePartialPaymentAvailablePopup();
    if (
      ((isInvoiceFromJob && jobData[0]?.isEditable) || (estimateNumber && isEstimatePending)) &&
      hasDataChangesInInvoice
    ) {
      setShowSyncConfirmationPopup(true);
    } else if (hasCustomerEmail) {
      setShowSendUpdateToCustomerModal(true);
    } else {
      handleEditInvoice();
    }
  }, [
    handleClosePartialPaymentAvailablePopup,
    isInvoiceFromJob,
    jobData,
    estimateNumber,
    isEstimatePending,
    hasDataChangesInInvoice
  ]);

  const handleEditInvoiceFromPartialPaymentPopup = useCallback(() => {
    handleClosePartialPaymentAvailablePopup();
  }, [handleClosePartialPaymentAvailablePopup]);

  const handleCloseInvoiceLimitExceededPopup = useCallback(() => {
    setShowInvoiceLimitExceededPopup(false);
  }, []);

  const handleEditInvoiceFromLimitExceededPopup = useCallback(() => {
    handleCloseInvoiceLimitExceededPopup();
  }, [handleCloseInvoiceLimitExceededPopup]);

  const editAndShareHandler = useCallback(
    throttle(() => {
      onEditAndShare();
    }, 800),
    [onEditAndShare]
  );

  const onBackIconClick = () => {
    dispatch(resetInvoiceEditSlice());
    navigate(-1);
  };

  const handleSignatureStateChange = (value: boolean) => {
    dispatch(updateIsSignatureEnabledEditPage(value));
  };

  const handleInvoiceReferenceNumberChange = (value: string) => {
    dispatch(updateInvoiceReferenceNumberEditPage(value));
  };

  const checkIsSignatureDisabled = useMemo(() => {
    if (merchantCustomisationsSuccess) {
      return merchantCustomisations.customerSignatureConfig === FeatureToggleState.ALWAYS_ON;
    }
    return false;
  }, [merchantCustomisationsSuccess, merchantCustomisations?.customerSignatureConfig]);

  const getSyncPopupTitleAndSubtitle = (type: 'title' | 'subtitle') => {
    const title = `Save and sync changes to the related ${isInvoiceFromJob || invoiceFromJobNumber ? 'job' : ''} ${
      (isInvoiceFromJob || invoiceFromJobNumber) && estimateNumber && isEstimatePending ? 'and' : ''
    } ${estimateNumber && isEstimatePending ? 'estimate' : ''}?`;
    const subtitle = `Changes in your invoices will be applied to the associated ${
      isInvoiceFromJob || invoiceFromJobNumber ? `job ${invoiceFromJobNumber}` : ''
    } ${(isInvoiceFromJob || invoiceFromJobNumber) && estimateNumber && isEstimatePending ? 'and' : ''} ${
      estimateNumber && isEstimatePending ? `estimate ${estimateNumber}` : ''
    } as well.`;
    return type === 'title' ? title : subtitle;
  };

  const onPreviewButtonClick = () => {
    let isPageIncomplete = false;
    if (selectedInvoice.invoiceLineItemsEditPage.length === 0) {
      isPageIncomplete = true;
      dispatch(addInvoiceValidationErrorEditPage(minimumLineItemsError));
    }

    if (selectedInvoice.invoiceCustomerEditPage === null) {
      isPageIncomplete = true;
      dispatch(enableCustomerNotSelectedErrorEditPage());
    }

    if (isPageIncomplete) {
      return;
    }
    goToPreviewPage();
  };

  if (isLoading || invoiceInitInProgress)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
      </div>
    );

  if (!isLoading && isGetInvoiceErr)
    return (
      <div className="flex h-full w-full justify-center">
        <GenericError handleErrorBack={() => navigate(-1)} />
      </div>
    );

  return (
    <EditInvoiceContext.Provider value={{ selectedInvoiceTab, setSelectedInvoiceTab }}>
      <div className="flex h-full w-full flex-col border">
        <PreviewBar
          className="border border-borderGray"
          title="Edit Invoice"
          onPrimaryClick={editAndShareHandler}
          onReturn={onBackIconClick}
          primaryBtnTitle="Save & Share"
          secondaryBtnTitle="Preview"
          disablePrimaryBtn={false}
          isPrimaryBtnLoading={isEditInvoiceLoading}
          onSecondaryClick={onPreviewButtonClick}
          primaryBtnId="save-and-share-invoice-button"
          secondaryBtnId="edit-invoice-preview-button"
        />
        <div className="flex h-full w-full justify-center overflow-hidden bg-primary">
          <div
            className="hidden w-1/3 max-w-[31.25rem] flex-col items-center p-12
          pr-[50px] lg:flex lg:w-[24rem] 1439px:w-[31.25rem]">
            <InvoicesTabSideNav context={EditInvoiceContext} isInvoiceEdit />
          </div>

          <div
            id="scrollable div"
            className="customNormalScroll flex grow flex-col space-y-[30px]
           overflow-y-auto p-12 pl-[50px] 1439px:space-y-11">
            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.BILL_TO}>
              <EditInvoiceCustomer context={EditInvoiceContext} />
            </div>

            {/* TODO invoice options in edit page */}
            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.INVOICE_OPTIONS}>
              <InvoiceOptionsSectionEdit context={EditInvoiceContext} />
            </div>

            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.LINE_ITEMS}>
              <LineItemsSectionEdit
                taxesAndFees={taxesAndFeesData}
                isLoadingTaxesAndFees={isLoadingTaxesAndFees}
                context={EditInvoiceContext}
              />
            </div>

            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.NOTES_ATTACHMENTS}>
              <NotesAttachmentsSectionEdit context={EditInvoiceContext} isLoadingTaxesAndFees={isLoadingTaxesAndFees} />
            </div>

            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.PAYMENT_OPTIONS}>
              <PaymentOptionsSectionEdit isInvoiceEdit={true} context={EditInvoiceContext} />
            </div>
            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.PAYMENT_REMINDERS}>
              <InvoiceFormRemindersSection isInvoiceEdit={true} context={EditInvoiceContext} />
            </div>
            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.MORE_OPTIONS}>
              <MoreOptionsSection
                signatureState={selectedIsSignatureEnabled}
                updateSignatureState={handleSignatureStateChange}
                context={EditInvoiceContext}
                isDisabled={checkIsSignatureDisabled}
                id="edit-invoice-allow-sign-toggle-switch"
                invoiceReferenceNumber={invoiceReferenceNumberEditPage}
                updateInvoiceReferenceNumber={handleInvoiceReferenceNumberChange}
              />
            </div>
          </div>
        </div>
      </div>
      {showSyncConfirmationPopup && (
        <GenericModal
          showModal={true}
          title={getSyncPopupTitleAndSubtitle('title')}
          titleStyle="text-center"
          subTitleStyle="text-center text-primaryText text-sbase font-normal"
          subTitle={getSyncPopupTitleAndSubtitle('subtitle')}
          primaryBtnLabel="Yes, continue"
          primaryBtnId="sync-job-invoice"
          primaryBtnStyle="mt-2"
          onPrimaryBtnClick={handleContinueFromSyncConfirmation}
          secondaryBtnId="cancel-sync-job-invoice"
          secondaryBtnStyle="border-none shadow-none hover:bg-transparent"
          secondaryBtnLabel="Cancel"
          onSecondaryBtnClick={() => setShowSyncConfirmationPopup(false)}
          closeIconStyle="top-3 right-3"
          CloseIcon={() => <CrossIconvector height={18} width={18} title="sync-popup-close" />}
          isActionInProgress={isEditInvoiceLoading}
          height="h-[262px]"
          className="!px-5 !py-6"
        />
      )}
      {showSendUpdateToCustomerModal && (
        <SendUpdateModal
          showModal={showSendUpdateToCustomerModal}
          onYesClick={() => handleUpdateToCustomerAndSave(true)}
          onNoClick={handleUpdateToCustomerAndSave}
          onClose={() => setShowSendUpdateToCustomerModal(false)}
          isActionInProgress={isEditInvoiceLoading}
        />
      )}
      {showNoSelfCheckoutPopup && (
        <NoSelfCheckoutPopup
          isEdit={true}
          onContinue={handleOnCreateInvoiceWithoutSelfCheckout}
          onEditPaymentMethods={handleOnEditPaymentMethods}
          onClose={handleCloseNoSelfCheckoutPopup}
          isActionLoading={isEditInvoiceLoading}
        />
      )}
      {showInvoiceErrorsPopup.open && (
        <InvoiceErrorsPopup
          onClose={handleCloseInvoiceErrorsPopup}
          errorFields={showInvoiceErrorsPopup.errors}
          maximumAmount={
            partialPaymentEnabled ? +invoiceLimits.invoiceMaxLimit : +merchantCustomisations?.maximumAmount || 0
          }
          minimumAmount={
            partialPaymentEnabled ? +invoiceLimits.invoiceMinLimit : +merchantCustomisations?.minimumAmount || 0
          }
          onContinue={handleCloseInvoiceErrorsPopup}
        />
      )}
      {showMerchantBlockedPopup && (
        <BlockedInfoPopup isOpen={showMerchantBlockedPopup} onClose={() => setShowMerchantBlockedPopup(false)} />
      )}
      {showPartialPaymentAvailablePopup && (
        <PartialPaymentAvailablePopup
          onClose={handleClosePartialPaymentAvailablePopup}
          paymentModes={invoicePaymentModes}
          onCreateAnyway={handleSaveAnyway}
          onEditInvoice={handleEditInvoiceFromPartialPaymentPopup}
        />
      )}
      {showInvoiceLimitExceededPopup && (
        <InvoiceLimitExceededPopup
          onClose={handleCloseInvoiceLimitExceededPopup}
          invoiceLimits={invoiceLimits}
          onEditInvoice={handleEditInvoiceFromLimitExceededPopup}
        />
      )}
      {showInvalidTotalModal && (
        <InvalidInvoiceTotalModal
          open={showInvalidTotalModal}
          onClose={() => setShowInvalidTotalModal(false)}
          totalPaid={netPaidAmount}
          minimumTotal={netPaidAmount + 0.01}
        />
      )}
    </EditInvoiceContext.Provider>
  );
};

export default EditInvoice;
